import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, AlertCircle, ExternalLink } from "lucide-react";

/**
 * AddressMapPreview — live OpenStreetMap preview for the Customer
 * Edit dialog address cards.
 *
 *  • Debounces incoming address changes for 1200ms before hitting
 *    Nominatim (within OSM's 1 req/s fair-use guideline).
 *  • Caches the last successful geocode by query string so editing a
 *    single character does not retrigger the fetch.
 *  • Renders a 110px-tall iframe with a marker over the resolved
 *    coordinates. Falls back to «Δεν βρέθηκε» when nothing matches
 *    and shows a helpful direct OSM link as a manual escape hatch.
 *
 *  Free service — no API key needed. We pass `countrycodes=gr` to
 *  restrict results to Greek addresses (avoids false matches in
 *  countries with similarly-named streets).
 */
export default function AddressMapPreview({ street, streetNo, area }) {
  const [state, setState] = useState({ status: "idle", lat: null, lon: null });
  // Persist last-resolved (query → coords) so re-mounts (e.g. parent
  // re-renders) do not re-fetch the same address.
  const cacheRef = useRef(new Map());

  // Build a stable, trimmed query — only geocode when there's actually
  // enough to disambiguate.
  const composed = [street, streetNo, area].map((s) => (s || "").trim()).filter(Boolean).join(" ");
  const query = composed.length >= 4 ? composed : "";

  useEffect(() => {
    if (!query) {
      setState({ status: "idle", lat: null, lon: null });
      return;
    }
    // Cache hit → resolve instantly, no network.
    const cached = cacheRef.current.get(query);
    if (cached) {
      setState({ status: "ok", lat: cached.lat, lon: cached.lon });
      return;
    }
    setState((s) => ({ ...s, status: "loading" }));
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=gr&limit=1&q=${encodeURIComponent(query)}`;
        const r = await fetch(url, { signal: ctrl.signal, headers: { Accept: "application/json" } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!Array.isArray(data) || data.length === 0) {
          setState({ status: "not_found", lat: null, lon: null });
          return;
        }
        const { lat, lon } = data[0];
        const fLat = parseFloat(lat);
        const fLon = parseFloat(lon);
        if (!Number.isFinite(fLat) || !Number.isFinite(fLon)) {
          setState({ status: "not_found", lat: null, lon: null });
          return;
        }
        cacheRef.current.set(query, { lat: fLat, lon: fLon });
        setState({ status: "ok", lat: fLat, lon: fLon });
      } catch (e) {
        if (e.name === "AbortError") return;
        setState({ status: "error", lat: null, lon: null });
      }
    }, 1200);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query]);

  if (!query) {
    return (
      <div className="mt-1.5 h-[88px] rounded border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[10px] text-slate-400 italic gap-1.5" data-testid="addr-map-empty">
        <MapPin size={12} /> Συμπλήρωσε διεύθυνση για προεπισκόπηση χάρτη
      </div>
    );
  }
  // The `useEffect` runs AFTER commit, so the first render with a fresh
  // `query` but stale `status:"idle"` would otherwise fall through to
  // the populated branch and crash on `state.lat.toFixed(...)`. Treat
  // any non-ok / missing-coords state explicitly as «loading».
  if (state.status === "loading" || state.status === "idle" || state.lat === null || state.lon === null) {
    return (
      <div className="mt-1.5 h-[88px] rounded border border-slate-300 bg-slate-50 flex items-center justify-center text-[10px] text-slate-500 gap-1.5" data-testid="addr-map-loading">
        <Loader2 size={12} className="animate-spin" /> Αναζήτηση «{query}»...
      </div>
    );
  }
  if (state.status === "not_found" || state.status === "error") {
    return (
      <div className="mt-1.5 h-[88px] rounded border border-amber-200 bg-amber-50 flex flex-col items-center justify-center text-[10px] text-amber-800 gap-0.5 px-2" data-testid="addr-map-not-found">
        <div className="flex items-center gap-1.5">
          <AlertCircle size={12} />
          {state.status === "error" ? "Σφάλμα δικτύου" : "Δεν βρέθηκε στον χάρτη"}
        </div>
        <a
          href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(query)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-amber-900 underline flex items-center gap-1 font-bold"
        >
          Αναζήτηση στο OSM <ExternalLink size={10} />
        </a>
      </div>
    );
  }

  // status === "ok"
  // bbox: ~600m around the marker for a useful neighbourhood view.
  const dLat = 0.003;
  const dLon = 0.004;
  const bbox = [state.lon - dLon, state.lat - dLat, state.lon + dLon, state.lat + dLat].join(",");
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${state.lat},${state.lon}`;
  return (
    <div className="mt-1.5" data-testid="addr-map-preview">
      <iframe
        title={`Χάρτης: ${query}`}
        src={src}
        className="w-full h-[88px] rounded border border-slate-300"
        loading="lazy"
        sandbox="allow-scripts allow-same-origin allow-popups"
      />
      <div className="flex items-center justify-between text-[9px] text-slate-500 mt-0.5 font-mono">
        <span>{state.lat.toFixed(5)}, {state.lon.toFixed(5)}</span>
        <a
          href={`https://www.openstreetmap.org/?mlat=${state.lat}&mlon=${state.lon}#map=17/${state.lat}/${state.lon}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700 underline flex items-center gap-0.5"
        >
          Άνοιγμα σε OSM <ExternalLink size={9} />
        </a>
      </div>
    </div>
  );
}
